import { useMemo } from 'react';

import isNumber from 'lodash/isNumber';
import sum from 'lodash/sum';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@platform/core/feature-flags';

import { useAssetStats } from '@apis/asset-data-service/queries/use-asset-stats';
import {
  PageHeaderV2,
  type PageHeaderV2Props,
} from '@packages/components/page/page-header-v2/page-header-v2';

export interface AssetListPageHeaderProps
  extends Omit<PageHeaderV2Props, 'title' | 'documentTitle'> {
  total?: number;
  totalLoading?: boolean;
}

declare module '@platform/core/feature-flags' {
  interface FeatureFlags {
    UNIFIED_PORTAL_IA?: boolean;
  }
}

export const AssetListPageHeader = (props: AssetListPageHeaderProps) => {
  const { t } = useTranslation('ads');
  const [unifiedPortalIaFF] = useFeatureFlag('UNIFIED_PORTAL_IA');
  const { data: stats, isLoading, isError } = useAssetStats();
  const { total, totalLoading, ...rest } = props;

  const statsSum = useMemo(
    () => (stats ? sum(Object.values(stats)) : 0),
    [stats],
  );

  const entries = useMemo(() => {
    if (isError) {
      return '';
    }

    if (isLoading || totalLoading) {
      return '(...)';
    }

    if (isNumber(total)) {
      return `(${total})`;
    }

    return `(${statsSum})`;
  }, [isError, isLoading, statsSum, total, totalLoading]);

  const title = useMemo(
    () => (unifiedPortalIaFF ? t('assets') : t('myAssets')),
    [t, unifiedPortalIaFF],
  );

  return (
    <PageHeaderV2
      title={`${title} ${entries}`}
      documentTitle={title}
      {...rest}
    />
  );
};
